import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import BreadcrumbsComponent from "../../../Components/BreadCrumbs";
import LeftSideNav from "../../../Components/LeftSideNav";
import TopNavBar from "../../../Components/TopNavbar";
import Fonts from "../../../Config/Fonts";
import Strings from "../../../Config/Strings";
import { AppConstants } from "../../../Constants/AppConstants";
import {
  getAllOrderListAPI,
  getOrderByManufactureIdAPI,
} from "../../../Store/Action/orderAction";
import Colors from "../../../theme/Colors";
import OrderTable from "./Controller/OrderTable";
import { getLoggedInUserDetailsAPI } from "../../../Store/Action/UserAction";
import { useUserRolePermission } from "../../../Context/UserRolePermission/UserRolePermissionState";
import { formatDateToAPI } from "../../../Utils";
import CustomTopView from "../../../Components/CustomTopView";

const OrderList = () => {
  const searchInputRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userData } = useSelector((state) => state.user);

  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [totalCount, setTotalCount] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [stateName, setStateName] = useState(null); // State filter
  const [cityName, setCityName] = useState(null); // City filter
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const limit = AppConstants.limit;
  const rowsPerPageOptions = [25, 50, 100];
  const rowsPerPageLabel = "Orders Per Page";

  const handleChangePage = useCallback((event, newPage) => {
    setPageNumber(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(event.target.value);
    setPageNumber(0);
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
    setPageNumber(0); // Reset to the first page when search changes
  }, []);

  const recordAfterSorting = orderData?.slice(
    pageNumber * rowsPerPage,
    (pageNumber + 1) * rowsPerPage
  );

  useEffect(() => {
    setBreadcrumbs([{ key: "1", href: "/orderList", text: "Orders" }]);
    dispatch(getLoggedInUserDetailsAPI({}));
  }, [dispatch]);

  const formattedStartDate = formatDateToAPI(dateRange?.startDate);
  const formattedEndDate = formatDateToAPI(dateRange?.endDate);

  useEffect(() => {
    // const OrderDataAPI = async () => {
    try {
      const params = {
        searchQuery: searchQuery || undefined,
        page: pageNumber || undefined,
        limit: limit || undefined,
      };

      const dateParams = dateRange?.startDate !== null &&
        dateRange?.endDate !== null && {
          start_date: formattedStartDate,
          end_date: formattedEndDate,
        };

      const stateCityParams = stateName !== null &&
        cityName !== null && {
          state: stateName,
          city: cityName,
        };

      if (userData?.data?.[0]?.user_role_name === Strings.Admin) {
        dispatch(
          getAllOrderListAPI(params, dateParams, stateCityParams, (result) => {
            setOrderData(result?.data || []);
            setTotalCount(result?.totalCount);

            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          })
        );
      } else {
        dispatch(
          getOrderByManufactureIdAPI(userData?.data?.user_role_id, (result) => {
            setOrderData(result?.data);
          })
        );
      }
    } catch (error) {
      console.log("Failed to fetch orders:", error);
    }
    // };
    // OrderDataAPI();
    // }, [
    //   dispatch,
    //   limit,
    //   pageNumber,
    //   searchQuery,
    //   dateRange,
    //   userData?.data?.user_role_id,
    //   userData?.data?.user_role_name,
    //   formattedStartDate,
    //   formattedEndDate,
    //   stateName,
    //   cityName,
    // ]);
  }, []);
  const onViewOrderDetails = useCallback(
    (orderData) => {
      navigate(`/orderList/${orderData?.order_id}`, { state: { orderData } });
    },
    [navigate]
  );

  const userRolePermission = useUserRolePermission();

  // Handle filter change
  const handleFilterChange = useCallback((filters) => {
    setStateName(filters.stateName);
    setCityName(filters.cityName);
    setDateRange(filters.dateRange);
    setPageNumber(0); // Reset to the first page when filters change
  }, []);

  return (
    <div style={mainContainerStyle}>
      <LeftSideNav />

      <div style={topContainerStyle}>
        <TopNavBar
          leftSideText={`Hello ${userData?.data?.user_first_name}`}
          isDashboard={true}
        />

        <CustomTopView
          breadcrumbs={breadcrumbs}
          exportButtonText={"Export Orders"}
          rowsPerPage={rowsPerPage}
          handleRowsPerPageChange={handleChangeRowsPerPage}
          rowPerPageLabel={rowsPerPageLabel}
          searchValue={searchQuery}
          onSearchChange={handleSearchChange}
          isOrder={true}
        />

        <div style={tableContainerStyle}>
          <OrderTable
            orderData={recordAfterSorting}
            onViewOrderDetails={onViewOrderDetails}
            page={pageNumber}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalItems={totalCount}
            rowsPerPageOptions={rowsPerPageOptions}
            searchValue={searchQuery}
            onSearchChange={handleSearchChange}
            rowsPerPageLabel={rowsPerPageLabel}
            orderRolePermissions={userRolePermission} // Pass role permissions here
            onFilterChange={handleFilterChange} // Handle filter change
          />
        </div>
      </div>
    </div>
  );
};

const mainContainerStyle = {
  display: "grid",
  gridTemplateColumns: "20% 80%",
  height: "100vh",
  width: "100vw",
  overflowY: "hidden",
};

const topContainerStyle = {
  backgroundColor: Colors.lightBlue,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  paddingRight: "20px",
};

const tableContainerStyle = {
  paddingTop: "20px",
  width: "100%",
  height: "70vh",
  marginTop: "15px",
  marginLeft: "20px",
};

const mainHeadingView = {
  marginLeft: "30px",
  justifyContent: "flex-start",
  display: "flex",
};

const mainHeadingStyle = {
  fontSize: 24,
  fontWeight: 700,
  fontFamily: Fonts.NunitoSans,
  color: Colors.black,
};

export default OrderList;
